import { compact, orderBy, uniq } from "lodash";
import { GetMeetingTranscriptQueryQuery } from "types/graphql-schema";

import Tooltip from "@components/tooltip/tooltip";

const TranscriptionParticipation = ({
  meeting,
}: {
  meeting: NonNullable<GetMeetingTranscriptQueryQuery["meeting"]>;
}) => {
  const orderedTranscriptData = compact(
    meeting.transcript?.orderedTranscriptData || [],
  );
  const userNames = uniq(
    orderedTranscriptData.map((transcriptData) => transcriptData?.speaker),
  );

  const uniqUsers = userNames.map((user) => {
    const text = orderedTranscriptData
      .filter((transcriptData) => transcriptData.speaker === user)
      .map((transcriptData) => transcriptData.text)
      .join(" ");
    return {
      name: user,
      text,
      wordCount: text.trim().split(/\s+/).length,
    };
  });
  const totalWordCount = uniqUsers.reduce(
    (memo, user) => memo + user.wordCount,
    0,
  );
  const uniqUsersOrderByWordCountDesc = orderBy(
    uniqUsers,
    "wordCount",
  ).reverse();

  const participants = uniqUsersOrderByWordCountDesc.map((user) => ({
    name: user.name || "unknown",
    value: (user.wordCount / totalWordCount) * 100,
    valueLabel: Math.round((user.wordCount / totalWordCount) * 100),
  }));
  const participantsThatTalked = participants.filter(
    (participant) => participant.value >= 1,
  );
  const participantsThatTalkedALittleBit = participants.filter(
    (participant) => participant.value < 1 && participant.value > 0,
  );
  const displayedParticipantCount =
    participantsThatTalked.length + participantsThatTalkedALittleBit.length;

  const COLOURS = [
    "#1f77b4",
    "#ff7f0e",
    "#2ca02c",
    "#d62728",
    "#9467bd",
    "#8c564b",
    "#e377c2",
    "#7f7f7f",
    "#bcbd22",
    "#17becf",
    "#aec7e8",
    "#ffbb78",
    "#98df8a",
    "#ff9896",
    "#c5b0d5",
    "#c49c94",
    "#f7b6d2",
    "#c7c7c7",
    "#dbdb8d",
    "#9edae5",
    "#f0f0f0",
    "#ff6600",
    "#33cc33",
    "#ff3300",
    "#6600ff",
    "#cc00cc",
    "#00ccff",
    "#ffcc00",
    "#99ff99",
    "#ff99cc",
    "#99ccff",
    "#ccff99",
    "#ffccff",
    "#ff9999",
    "#cc6600",
    "#ff6666",
    "#33ffcc",
    "#ff6600",
    "#cc33ff",
    "#6699ff",
    "#ff3300",
    "#33ccff",
    "#ff9966",
    "#ff0066",
    "#00ff99",
    "#ffcc99",
    "#0099ff",
    "#ff3333",
    "#33ff99",
  ];

  if (displayedParticipantCount === 0) {
    return null;
  }

  return (
    <div>
      <div className="font-semibold text-gray-900 mb-2">
        Participation Breakdown
      </div>
      <ul className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
        {participantsThatTalked.map(({ name, valueLabel }, i) => (
          <li key={name} className="flex items-center gap-1">
            <span
              className="inline-block w-2 h-2 rounded-full"
              style={{ backgroundColor: COLOURS[i % participants.length] }}
              aria-hidden="true"
            />
            <span style={{ color: COLOURS[i % participants.length] }}>
              {name}
            </span>
            <Tooltip text={`${valueLabel} % participation`}>
              <span className="text-gray-500 tracking-tight">
                ({valueLabel}%)
              </span>
            </Tooltip>
          </li>
        ))}
        {participantsThatTalkedALittleBit.length > 0 && (
          <li className="flex items-center gap-1">
            <Tooltip
              text={`${participantsThatTalkedALittleBit
                .map(({ name }) => name)
                .join(",")}`}
            >
              <span className="text-gray-800">
                {participantsThatTalkedALittleBit.length === 1
                  ? participantsThatTalkedALittleBit[0].name
                  : `${participantsThatTalkedALittleBit.length} participants`}
              </span>
            </Tooltip>
            <Tooltip
              text={`${participantsThatTalkedALittleBit
                .map(({ name }) => name)
                .join(",")}`}
            >
              <span className="text-gray-500 tracking-tight">({"<"}1%)</span>
            </Tooltip>
          </li>
        )}
      </ul>
    </div>
  );
};

export default TranscriptionParticipation;
